import { useMemo, useState } from "react";
import { Plus, Trash2, Save, X, Phone, Mail, MapPin, MessageCircle, User } from "lucide-react";
import api from "../lib/api";
import { waLink } from "../lib/constants";
import ConfirmDialog from "../components/ConfirmDialog";

export default function ClientesView({ clients, orders, onRefresh }) {
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // client id or "new"
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", notes: "" });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    if (!s) return clients;
    return clients.filter(
      (c) =>
        c.name.toLowerCase().includes(s) ||
        c.phone.includes(q) ||
        (c.email || "").toLowerCase().includes(s)
    );
  }, [clients, q]);

  const ordersByClient = useMemo(() => {
    const m = {};
    orders.forEach((o) => {
      const k = o.clientId || o.clientPhone;
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [orders]);

  const startEdit = (c) => {
    setEditing(c.id);
    setForm({ name: c.name, phone: c.phone, email: c.email || "", address: c.address || "", notes: c.notes || "" });
  };
  const startNew = () => {
    setEditing("new");
    setForm({ name: "", phone: "", email: "", address: "", notes: "" });
  };
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!form.name.trim() || !form.phone.trim()) return;
    if (editing === "new") await api.post("/clients", form);
    else await api.patch(`/clients/${editing}`, form);
    setEditing(null);
    onRefresh();
  };

  const del = async (id) => {
    await api.delete(`/clients/${id}`);
    onRefresh();
  };

  return (
    <div className="p-6 space-y-4" data-testid="clientes-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Clientes</h2>
          <p className="text-sm text-slate-500 mt-0.5">{clients.length} clientes cadastrados</p>
        </div>
        <div className="flex gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome, telefone..."
            className="h-10 rounded-lg border border-slate-300 px-3 text-sm w-64 focus:border-blue-500 focus:outline-none"
            data-testid="client-search"
          />
          <button
            onClick={startNew}
            data-testid="new-client-button"
            className="h-10 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold inline-flex items-center gap-2 transition-all hover:-translate-y-[1px] hover:shadow-lg"
          >
            <Plus className="w-4 h-4" /> Novo cliente
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {editing === "new" && (
          <ClientForm form={form} setForm={setForm} onSave={save} onCancel={cancel} isNew />
        )}
        {filtered.map((c) =>
          editing === c.id ? (
            <ClientForm key={c.id} form={form} setForm={setForm} onSave={save} onCancel={cancel} />
          ) : (
            <div
              key={c.id}
              className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-[1px] transition-all group"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-700" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-slate-900 truncate">{c.name}</div>
                    <div className="text-[11px] text-slate-400">
                      {ordersByClient[c.id] || 0} OS
                    </div>
                  </div>
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-0.5">
                  <button
                    onClick={() => startEdit(c)}
                    className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                    title="Editar"
                  >
                    <Save className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setConfirmDelete(c)}
                    className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded"
                    title="Excluir"
                    data-testid={`delete-client-${c.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-slate-400" />
                  {c.phone}
                  <a
                    href={waLink(c.phone, `Olá ${c.name}!`)}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-auto text-emerald-600 hover:text-emerald-700"
                    title="WhatsApp"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                  </a>
                </div>
                {c.email && (
                  <div className="flex items-center gap-2 truncate">
                    <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">{c.email}</span>
                  </div>
                )}
                {c.address && (
                  <div className="flex items-center gap-2 truncate">
                    <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">{c.address}</span>
                  </div>
                )}
                {c.notes && (
                  <div className="mt-1 text-[11px] italic text-slate-400 border-l-2 border-slate-200 pl-2">
                    {c.notes}
                  </div>
                )}
              </div>
            </div>
          )
        )}
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Excluir cliente"
        message={confirmDelete ? `Excluir o cliente "${confirmDelete.name}"?` : ""}
        confirmLabel="Excluir"
        onCancel={() => setConfirmDelete(null)}
        onConfirm={async () => {
          const id = confirmDelete.id;
          setConfirmDelete(null);
          await api.delete(`/clients/${id}`);
          onRefresh();
        }}
      />
    </div>
  );
}

function ClientForm({ form, setForm, onSave, onCancel, isNew }) {
  return (
    <div className="bg-blue-50/50 rounded-xl border-2 border-blue-300 border-dashed p-4">
      <div className="text-[11px] uppercase font-semibold text-blue-700 tracking-wider mb-2">
        {isNew ? "Novo cliente" : "Editar cliente"}
      </div>
      <div className="space-y-2">
        <input
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="Nome *"
          className="w-full h-9 rounded border border-slate-300 px-2.5 text-sm focus:border-blue-500 focus:outline-none"
          data-testid="client-form-name"
        />
        <input
          value={form.phone}
          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          placeholder="Telefone *"
          className="w-full h-9 rounded border border-slate-300 px-2.5 text-sm focus:border-blue-500 focus:outline-none"
          data-testid="client-form-phone"
        />
        <input
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          placeholder="Email"
          className="w-full h-9 rounded border border-slate-300 px-2.5 text-sm focus:border-blue-500 focus:outline-none"
        />
        <input
          value={form.address}
          onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
          placeholder="Endereço"
          className="w-full h-9 rounded border border-slate-300 px-2.5 text-sm focus:border-blue-500 focus:outline-none"
        />
        <textarea
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          placeholder="Observações"
          rows={2}
          className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
        />
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={onCancel} className="h-8 px-3 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-200">
          <X className="w-3.5 h-3.5 inline mr-1" /> Cancelar
        </button>
        <button
          onClick={onSave}
          data-testid="client-form-save"
          className="h-8 px-3 rounded-md text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1"
        >
          <Save className="w-3.5 h-3.5" /> Salvar
        </button>
      </div>
    </div>
  );
}
